import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { useSettingsFilters } from '../hooks';
import { matterPracticeAreasApi, settingsQueryKeys } from '../api';
import { SettingsDataTable, MatterPracticeAreaAction, useSettingsColumns } from '../components';
import type { MatterPracticeArea } from '../types';

export function MatterPracticeAreasPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, setPage, setLimit, handleSort } = useSettingsFilters();

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: settingsQueryKeys.matterPracticeAreas.list(filters),
    queryFn: () => matterPracticeAreasApi.getAll(filters),
  });

  const columns = useSettingsColumns<MatterPracticeArea>({
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    resource: 'matter-practice-area',
    apiEndpoint: 'Matter-Practice-Areas',
    queryKey: 'matterPracticeAreas',
    title: t('matterPracticeArea'),
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('matterPracticeArea')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('matter', 'get') && (
            <Link to="/app/matters">
              <Button variant="outline">{t('matters')}</Button>
            </Link>
          )}
          {hasAccess('matter-practice-area', 'create') && (
            <MatterPracticeAreaAction />
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <SettingsDataTable
          tableZoom={tableZoom}
          tableId="matter-practice-areas"
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          emptyMessage={t('noMatterPracticeAreas')}
          createButton={
            hasAccess('matter-practice-area', 'create') ? (
              <MatterPracticeAreaAction />
            ) : undefined
          }
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
